import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type NotificationType =
  | 'trade_executed'
  | 'position_liquidated'
  | 'price_alert'
  | 'achievement'
  | 'funding_payment';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  description: string;
  timestamp: number;
  read: boolean;
  meta?: Record<string, unknown>;
}

interface NotificationStore {
  notifications: AppNotification[];
  addNotification: (n: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => void;
  markAllRead: () => void;
  markRead: (id: string) => void;
  clearAll: () => void;
  unreadCount: () => number;
}

const MAX_NOTIFICATIONS = 50;

export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set, get) => ({
      notifications: [],

      addNotification: (n) =>
        set((state) => {
          const notif: AppNotification = {
            ...n,
            id: `n-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            timestamp: Date.now(),
            read: false,
          };
          const next = [notif, ...state.notifications].slice(0, MAX_NOTIFICATIONS);
          return { notifications: next };
        }),

      markAllRead: () =>
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
        })),

      markRead: (id) =>
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
        })),

      clearAll: () => set({ notifications: [] }),

      unreadCount: () => get().notifications.filter((n) => !n.read).length,
    }),
    {
      name: 'whether-notifications',
      partialize: (state) => ({ notifications: state.notifications }),
    }
  )
);
